import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { 
  getClinicalAssessment, 
  getClinicalSoapNotes,
  getClinicalPrescription,
  getClinicalDischargeSummary,
  getBilling,
  getExternalRecords,
  getClinicalLabResults,
  downloadClinicalAssessmentDocument
} from "../../../api/apiService";
import { getPatientById } from "../../../Services/patientService";
import { printDocumentHtml, buildVisitSummaryHtml, resolveHospitalName } from "../../../utils/printEngine";

const formatFileSize = (bytes) => {
    if (bytes == null || bytes === "" || isNaN(bytes)) return "";
    const num = Number(bytes);
    if (num <= 0) return "";
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    if (num < 1024 * 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(1)} MB`;
    return `${(num / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const VisitSummary = () => {
    const { user } = useAuth();
    const { visitId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        vitals: null,
        soapNotes: null,
        prescriptions: [],
        dischargeSummary: null,
        billing: null,
        labResults: [],
        externalRecords: [],
        immunizations: [],
        patient: location.state?.patientData || null
    });

    useEffect(() => {
        let isMounted = true;
        const fetchAll = async () => {
            setLoading(true);
            const queryParams = new URLSearchParams(location.search);
            const patientIdFromQuery = queryParams.get("patientId") || location.state?.patientId;
            let currentPatient = data.patient;

            const effectivePatientId = currentPatient?._id || currentPatient?.id || patientIdFromQuery;
            
            if (!effectivePatientId) {
                setLoading(false);
                return;
            }

            if (!currentPatient || !currentPatient.name) {
                try {
                    const fetchedPatient = await getPatientById(effectivePatientId);
                    if (fetchedPatient && isMounted) {
                        currentPatient = fetchedPatient;
                        setData((prev) => ({ ...prev, patient: fetchedPatient }));
                    }
                } catch (e) {
                    console.warn("Could not load patient for visit summary:", e);
                }
            }

            if (!visitId) {
                setLoading(false);
                return;
            }

            try {
                const [vitals, soap, rx, discharge, billing, labs, external] = await Promise.allSettled([
                    getClinicalAssessment(visitId, effectivePatientId),
                    getClinicalSoapNotes(visitId, effectivePatientId),
                    getClinicalPrescription(visitId, effectivePatientId),
                    getClinicalDischargeSummary(visitId, effectivePatientId),
                    getBilling(visitId),
                    getClinicalLabResults(visitId, effectivePatientId),
                    getExternalRecords(effectivePatientId, { visitId })
                ]);

                
                
                const assessmentRaw = vitals.status === 'fulfilled' ? (vitals.value?.data || vitals.value) : {};
                const assessmentVitals = assessmentRaw?.vitals || (vitals.value?.vitals) || (vitals.status === 'fulfilled' ? vitals.value : null);
                const immunizationObj = assessmentRaw?.immunization || {};
                  let filteredExternal = [];
                if (external.status === 'fulfilled') {
                    const allDocs = external.value?.data || external.value || [];
                    // Filter by visitId if present in the record, or date match as a fallback
                    filteredExternal = allDocs.filter(doc => 
                        (doc.visitId === visitId) || 
                        (doc.careContextReference === visitId)
                    );
                }

                const assessmentDocs = Array.isArray(assessmentRaw?.documentUploads) ? assessmentRaw.documentUploads : [];
                const mappedAssessmentDocs = assessmentDocs.map((doc, idx) => {
                    const rawDocName =
                        doc.originalname ||
                        doc.originalName ||
                        doc.name ||
                        doc.fileName ||
                        doc.filename ||
                        "";
                    const cleanDocName =
                        (typeof rawDocName === "string" && rawDocName.trim()
                            ? rawDocName
                                .replace(/^(?:files-)?\d{10,14}[-_]/, "")
                                .replace(/^[a-f0-9]{24,32}[-_]/, "")
                            : "") || `Document ${idx + 1}`;
                    return {
                        title: cleanDocName,
                        name: cleanDocName,
                        createdAt: doc.uploadDate,
                        date: doc.uploadDate,
                        size: formatFileSize(doc.size) || doc.mimeType || "Clinical Document",
                        isAssessmentDoc: true,
                        docIndex: idx,
                        visitId: visitId,
                        patientId: effectivePatientId,
                        fileUrl: doc.fileUrl,
                    };
                });
                const combinedHealthDocs = [...filteredExternal, ...mappedAssessmentDocs];

                const mappedImmunizations = Object.entries(immunizationObj).map(([key, value]) => ({
                    name: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                    date: value.date,
                    details: `${value.manufacturer || ""} ${value.lotNumber ? `(Lot: ${value.lotNumber})` : ""}`.trim()
                })).filter(im => im.date);

                setData(prev => ({
                    ...prev,
                    vitals: assessmentVitals,
                    soapNotes: soap.status === 'fulfilled' ? (soap.value?.data?.soapNotes || soap.value?.soapNotes || soap.value?.data) : null,
                    prescriptions: rx.status === 'fulfilled' ? (rx.value?.data?.medications || rx.value?.medications || rx.value?.data || []) : [],
                    dischargeSummary: discharge.status === 'fulfilled' ? (discharge.value?.data || discharge.value) : null,
                    billing: billing.status === 'fulfilled' ? (billing.value?.data || billing.value) : null,
                    labResults: labs.status === 'fulfilled' ? (rx.value?.data?.results || rx.value?.data || []) : [],
                    externalRecords: combinedHealthDocs,
                    immunizations: mappedImmunizations
                }));
            } catch (err) {
                console.error("Error fetching visit summary data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [visitId]);

    const formatDate = (dateString) => {
        if (!dateString) return "---";
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? "---" : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const handlePrintSummary = () => {
        const html = buildVisitSummaryHtml({
            hospitalName: resolveHospitalName(user?.hospital_name),
            patient: data.patient || {},
            visitData: data,
            visitId: visitId || ""
        });
        printDocumentHtml({
            title: `Visit Summary - ${data.patient?.name || "Patient"} - ${visitId || ""}`,
            contentHtml: html
        });
    };

    if (loading) return <div className="p-5 text-center"><div className="spinner-border text-primary" role="status"></div><div className="mt-2">Loading Real-Time Visit Summary...</div></div>;

    return (
        <div className="visit-summary-container p-4">
            {/* Breadcrumb & Top Actions */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                            <a href="#" onClick={(e) => { e.preventDefault(); navigate(-1); }} className="text-decoration-none text-secondary">Visit History</a>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            Visit Summary - {formatDate(data.soapNotes?.createdAt || data.patient?.visits?.find(v => (v.visitId || v._id) === visitId)?.createdAt || new Date())}
                        </li>
                    </ol>
                </nav>
                <div className="d-flex gap-2">
                    <button 
                        type="button"
                        className="btn btn-outline-secondary bg-white px-3 fw-bold small py-2 d-flex align-items-center gap-2" 
                        style={{ border: '1.5px solid #CBD5E1', color: '#334155' }}
                        onClick={handlePrintSummary}
                        title="Print Clinical Encounter Summary"
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                            <rect x="6" y="14" width="12" height="8"></rect>
                        </svg>
                        <span>Print Summary</span>
                    </button>
                </div>
            </div>

            <div className="row g-4">
                {/* 1. Prescriptions */}
                <div className="col-md-7">
                    <div className="summary-card h-100">
                        <div className="card-header-premium">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#EFF6FF" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path>
                                        <path d="m8.5 8.5 7 7"></path>
                                    </svg>
                                </span>
                                1. Prescriptions
                            </div>
                            <span className="badge-pill-orange">{data.prescriptions?.length || 0} Items</span>
                        </div>
                        <div className="table-responsive mt-3">
                            <table className="premium-table-v2">
                                <thead>
                                    <tr>
                                        <th>MEDICATION</th>
                                        <th>DOSAGE</th>
                                        <th>DURATION</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.prescriptions && data.prescriptions.length > 0 ? (
                                        data.prescriptions.map((p, i) => (
                                            <tr key={i}>
                                                <td className="fw-bold">{p.medicineName || p.name}</td>
                                                <td>{p.dosage || p.frequency || "---"}</td>
                                                <td>{p.duration || "---"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="3" className="py-4 text-center text-secondary small">No prescriptions found for this visit.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* 2. Diagnostic Reports */}
                <div className="col-md-5">
                    <div className="summary-card h-100">
                        <div className="card-header-premium">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#F5F3FF" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 18h8"></path>
                                        <path d="M3 22h18"></path>
                                        <path d="M14 22a7 7 0 1 0 0-14h-1"></path>
                                        <path d="M9 14h2"></path>
                                        <path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z"></path>
                                    </svg>
                                </span>
                                2. Diagnostic Reports
                            </div>
                        </div>
                        <div className="reports-list-v2 mt-3">
                            {data.labResults && data.labResults.length > 0 ? (
                                data.labResults.map((r, i) => (
                                    <div className="report-item-v2" key={i}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="icon-doc" style={{ color: "#64748B", display: "flex", alignItems: "center" }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="t">{r.testName || r.name || "Lab Report"}</div>
                                                <div className={`s ${r.status === 'Completed' ? 'text-success' : 'text-warning'}`}>{r.status || "Processing"}</div>
                                            </div>
                                        </div>
                                        <div className="action text-primary fw-bold small">View ↗</div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-secondary small py-4 text-center border rounded-3 bg-light opacity-75">No diagnostic reports linked to this visit.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 3. OP Consultations */}
                <div className="col-md-12">
                    <div className="summary-card h-100">
                        <div className="card-header-premium">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#ECFDF5" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                                        <path d="M9 12h6"></path>
                                        <path d="M9 16h6"></path>
                                    </svg>
                                </span>
                                3. OP Consultations
                            </div>
                        </div>
                        <div className="row mt-3 g-3">
                            <div className="col-md-4">
                                <div className="label-sm mb-2">SYMPTOMS</div>
                                <div className="consult-box blue">
                                    {data.soapNotes?.subjective || data.soapNotes?.complaint || "No specific symptoms recorded."}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="label-sm mb-2">DIAGNOSIS</div>
                                <div className="consult-box orange">
                                    {data.soapNotes?.assessment || data.soapNotes?.diagnosis || "No diagnosis assigned."}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="label-sm mb-2">DOCTOR'S NOTES</div>
                                <div className="consult-box indigo">
                                    {data.soapNotes?.plan || data.soapNotes?.notes || "No additional notes recorded."}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. Discharge Summaries */}
                <div className="col-md-7">
                    <div className="summary-card h-100">
                        <div className="card-header-premium">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#F0F9FF" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0284C7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                        <polyline points="7 3 7 8 15 8"></polyline>
                                    </svg>
                                </span>
                                4. Discharge Summaries
                            </div>
                        </div>
                        {data.dischargeSummary ? (
                             <div className="mt-3 p-3 border rounded-3 bg-light">
                                 <div className="item-title fw-bold mb-2">Summary of Hospitalization</div>
                                 <div className="small text-secondary">{data.dischargeSummary.summary || "Summary details available."}</div>
                             </div>
                        ) : (
                            <div className="discharge-empty-box mt-3">
                                <div className="info-circle">i</div>
                                <p>Not applicable for this OPD visit. This patient was not admitted.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* 5. Immunization Records */}
                <div className="col-md-5">
                    <div className="summary-card h-100">
                        <div className="card-header-premium">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#FFFBEB" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m18 2 4 4"></path>
                                        <path d="m17 7 3-3"></path>
                                        <path d="M19 9 8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5"></path>
                                        <path d="m9 11 4 4"></path>
                                    </svg>
                                </span>
                                5. Immunization Records
                            </div>
                        </div>
                        <div className="immunization-list mt-3">
                            {data.immunizations && data.immunizations.length > 0 ? (
                                data.immunizations.map((r, i) => (
                                    <div className="im-item" key={i}>
                                        <div className="check-icon success">✓</div>
                                        <div>
                                            <div className="name">{r.name}</div>
                                            <div className="sub">Administered: {formatDate(r.date)} {r.details ? `| ${r.details}` : ""}</div>
                                        </div>
                                    </div>
                                ))
                            ) : data.externalRecords?.filter(r => r.type === 'Immunization').length > 0 ? (
                                data.externalRecords.filter(r => r.type === 'Immunization').map((r, i) => (
                                    <div className="im-item" key={i}>
                                        <div className="check-icon success">✓</div>
                                        <div>
                                            <div className="name">{r.title || r.name}</div>
                                            <div className="sub">Administered: {formatDate(r.date)} | {r.details || ""}</div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-secondary small py-4 text-center border rounded-3 bg-light opacity-75">No immunization records recorded.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 6. Health Documents */}
                <div className="col-md-7">
                    <div className="summary-card h-100">
                        <div className="card-header-premium">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#EEF2FF" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"></path>
                                    </svg>
                                </span>
                                6. Health Documents
                            </div>
                        </div>
                        <div className="mt-3">
                            {data.externalRecords && data.externalRecords.length > 0 ? (
                                data.externalRecords.map((doc, i) => (
                                    <div className="doc-item-v2 mb-2" key={i}>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="icon-doc" style={{ color: "#64748B", display: "flex", alignItems: "center" }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="name">{doc.title || doc.name || "Health Document"}</div>
                                                <div className="sub">Uploaded {formatDate(doc.createdAt || doc.date)} | {doc.size || "Unknown Size"}</div>
                                            </div>
                                        </div>
                                        <div 
                                            className="download-btn" 
                                            role="button" 
                                            title="Download document" 
                                            style={{ cursor: "pointer" }}
                                            onClick={async () => {
                                                if (doc.isAssessmentDoc) {
                                                    try {
                                                        await downloadClinicalAssessmentDocument(doc.visitId, doc.docIndex, doc.patientId, doc.name);
                                                    } catch (dErr) {
                                                        console.error("Failed to download doc:", dErr);
                                                    }
                                                } else if (doc.fileUrl || doc.url) {
                                                    window.open(doc.fileUrl || doc.url, "_blank");
                                                }
                                            }}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-secondary small py-4 text-center border rounded-3 bg-light opacity-75">No additional health documents found.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 7. Wellness Records */}
                <div className="col-md-5">
                    <div className="summary-card h-100">
                        <div className="card-header-premium">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#FFF1F2" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E11D48" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                                    </svg>
                                </span>
                                7. Wellness Records
                            </div>
                        </div>
                        <div className="row g-2 mt-3">
                            <div className="col-6">
                                <div className="wellness-box">
                                    <div className="l">BLOOD PRESSURE</div>
                                    <div className="v">{data.vitals?.sbp ? `${data.vitals.sbp}/${data.vitals.dbp}` : "---"} <span className="u">mmHg</span></div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="wellness-box">
                                    <div className="l">BODY TEMP</div>
                                    <div className="v">{data.vitals?.temp || "---"} <span className="u">°F</span></div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="wellness-box">
                                    <div className="l">HEART RATE</div>
                                    <div className="v">{data.vitals?.pulse || "---"} <span className="u">bpm</span></div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="wellness-box">
                                    <div className="l">BMI</div>
                                    <div className="v">{data.vitals?.bmi || "---"} <span className="u"></span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 8. Invoices */}
                <div className="col-md-12">
                    <div className="summary-card invoice-card">
                        <div className="card-header-premium border-0">
                            <div className="title">
                                <span className="icon-wrap" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: "#ECFDF5" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                        <line x1="2" y1="10" x2="22" y2="10"></line>
                                    </svg>
                                </span>
                                8. Invoices
                            </div>
                            {data.billing?.status?.toUpperCase() === 'PAID' && <span className="badge-paid">PAID</span>}
                        </div>
                        {data.billing ? (
                            <div className="d-flex justify-content-between align-items-end mt-2">
                                <div>
                                    <div className="invoice-id">Invoice #{data.billing?.invoiceNo || data.billing?._id || "---"}</div>
                                    <div className="invoice-meta">Date: {formatDate(data.billing?.createdAt)} | Payment Mode: {data.billing?.paymentMode || "---"}</div>
                                </div>
                                <div className="text-end">
                                    <div className="total-label">Total Amount: <span className="amount">₹ {data.billing?.totalAmount || data.billing?.total || "0.00"}</span></div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-secondary small py-3 text-center border-top mt-2">No billing record found for this visit.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VisitSummary;
